import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getReservationReports } from '../../api/endpoints/reports';
import type { BookingStatus } from '../../api/types/common';
import { ApiErrorAlert } from '../../components/ApiErrorAlert';
import { ReservationIdentifierLink } from '../../components/ReservationIdentifierLink';
import { ReservationStatsSection } from './ReservationStatsSection';

const RECENT_LIMIT = 5;

/**
 * Recent-activity widget over the shared `Report/Reservations` endpoint (see
 * api/endpoints/reports.ts), fixed to `providerType: 'Air'`, last 90 days. Confirmed
 * live against the local dev DB that this combination returns real populated rows
 * (hundreds, in this sandbox) — client-side sorted by `bookedAt`/`prebookedAt` desc and
 * trimmed to the most recent `RECENT_LIMIT`. `status` selects which of the two Air lists
 * this mirrors: `'Prebooked'` for "Uçak Rezervasyonları" (in-progress, not yet ticketed —
 * matches "Rezervasyon Listesi"), `'Booked'` for "Uçak Biletleri" (actually issued
 * tickets — matches "Bilet Listesi"). The identifier column follows the same split as those two
 * full list pages: PNR for reservations, ticket/document number for tickets — both link
 * into `ReservationDetailPage` via `ReservationIdentifierLink`, exactly like the full
 * list pages do.
 */
function RecentAirCard({
  status,
  title,
  identifierField,
}: {
  status: BookingStatus;
  title: string;
  identifierField: 'pnr' | 'ticket';
}) {
  const query = useQuery({
    queryKey: ['dashboard', 'recentAir', status],
    queryFn: () =>
      getReservationReports({
        insertStartDate: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
        insertEndDate: dayjs().format('YYYY-MM-DD'),
        status,
        providerType: 'Air',
      }),
  });

  const rows = [...(query.data?.reservationReports ?? [])]
    .sort((a, b) => {
      // bookedAt/prebookedAt observed as "DD.MM.YYYY HH:mm:ss" — dayjs parses that with
      // an explicit format; invalid/missing values sort last.
      const parse = (v?: string) => (v ? dayjs(v, 'DD.MM.YYYY HH:mm:ss') : null);
      const da = parse(a.bookedAt || a.prebookedAt);
      const db = parse(b.bookedAt || b.prebookedAt);
      if (!da?.isValid()) return 1;
      if (!db?.isValid()) return -1;
      return db.valueOf() - da.valueOf();
    })
    .slice(0, RECENT_LIMIT);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          {title}
        </Typography>

        {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 1 }} />}
        {query.isPending && <CircularProgress size={20} />}

        {!query.isPending && !query.isError && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Bu aralıkta kayıt bulunamadı.
          </Typography>
        )}

        {rows.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{identifierField === 'ticket' ? 'Bilet No' : 'PNR'}</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Güzergah</TableCell>
                <TableCell>Ücret</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={(row.shoppingCartId as string | undefined) ?? index} hover>
                  <TableCell>
                    <ReservationIdentifierLink row={row} detailBasePath="/air/reservations" field={identifierField} />
                  </TableCell>
                  <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>{[row.origin, row.destination].filter(Boolean).join(' → ') || '—'}</TableCell>
                  <TableCell>{row.totalFare ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Anasayfa
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentAirCard status="Prebooked" title="Uçak Rezervasyonları" identifierField="pnr" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <RecentAirCard status="Booked" title="Uçak Biletleri" identifierField="ticket" />
        </Grid>
      </Grid>

      <ReservationStatsSection />
    </Box>
  );
}
